import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';

interface BackendOfflineScreenProps {
  isBackendUnreachable: boolean;
  retryCount: number;
  onRetry?: () => void;
}

export function BackendOfflineScreen(props: Readonly<BackendOfflineScreenProps>) {
  const { isBackendUnreachable, retryCount, onRetry } = props;
  const { t } = useTranslation('common');

  const isPersistentFailure = retryCount >= 20;
  const displayCount = retryCount - 2; // we don't display the first 3 attempts

  if (!isBackendUnreachable) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 420,
          width: '100%',
          p: 4,
          textAlign: 'center',
          borderRadius: 4,
        }}
      >
        <Stack spacing={3} alignItems="center">
          <WifiOffIcon sx={{ fontSize: 64, color: 'text.secondary' }} />

          <Typography variant="h5" fontWeight={600}>
            {t('offline.Server unreachable')}
          </Typography>

          {isPersistentFailure ? (
            <Typography variant="body1" color="text.secondary">
              {t(
                'offline.Unfortunately, the connection problem persists and we apologize for the inconvenience... Please try again later.'
              )}
            </Typography>
          ) : (
            <>
              <Typography variant="body1" color="text.secondary">
                {t('offline.We are trying to reconnect')}
              </Typography>

              <CircularProgress color="secondary" />

              <Typography variant="caption" color="text.secondary">
                {t('offline.Retry attempt', { count: displayCount })}
              </Typography>
            </>
          )}

          {onRetry && (
            <Button size="small" variant="contained" startIcon={<RefreshIcon />} onClick={onRetry}>
              {t('offline.Retry now')}
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
